import React, { useState } from 'react';
import ChatHeader from '../molecules/ChatHeader';
import MessageList from '../organisms/MessageList';
import ChatFooter from '../molecules/ChatFooter';
import Typography from '../atoms/Typography';
import Button from '../atoms/Button';
import { useChat } from '../../context/ChatContext';
import './ChatTemplate.css';

const ChatTemplate = ({ onBackClick, interviewerName = "AI Interviewer" }) => {
  const [showInstructions, setShowInstructions] = useState(true);
  const [showContentNotification, setShowContentNotification] = useState(true);
  
  const { 
    messages, 
    userAvatar, 
    teacherAvatar, 
    error, 
    isInitialized,
    hasPdfContent,
    hasTechRequirements,
    interviewComplete,
    interviewScore,
    interviewType,
    resetConversation,
    startConversation
  } = useChat();

  const handleRestartInterview = () => {
    resetConversation();
    // Wait a moment before restarting
    setTimeout(() => {
      startConversation();
    }, 500);
  };
  
  const handleCloseInstructions = () => {
    setShowInstructions(false);
  };
  
  const handleCloseContentNotification = () => {
    setShowContentNotification(false);
  };

  const hasRequiredContent = interviewType === 'behavioral' ? hasPdfContent : hasTechRequirements;

  return (
    <div className="chat-template">
      <ChatHeader 
        title={`${interviewType === 'technical' ? 'Technical' : 'Behavioral'} Interview Practice`}
        onBackClick={onBackClick}
        interviewer={interviewerName}
      />
      
      <div className="chat-content">
        {/* Show API Key form if not initialized */}
        {!isInitialized && (
          <div className="api-key-section">
            <Typography variant="h3" align="center" className="api-key-title">
              Interview server not configured
            </Typography>
            <Typography variant="body1" align="center" className="api-key-description">
              Set INTERVIEW_API_BASE_URL in config.js to your Vercel URL, then reload.
            </Typography>
          </div>
        )}

        {/* Show instructions */}
        {showInstructions && isInitialized && (
          <div className="instructions-overlay">
            <div className="instructions-modal glass-card">
              <Typography variant="h3" className="instructions-title">
                {interviewType === 'technical' ? 'Technical Interview Instructions' : 'Behavioral Interview Instructions'}
              </Typography>
              
              <div className="instructions-content">
                {interviewType === 'technical' ? (
                  <>
                    <Typography variant="body1" className="instruction-item">
                      🔧 <strong>Technical Focus:</strong> You'll be asked technical questions about the technologies and requirements you specified.
                    </Typography>
                    <Typography variant="body1" className="instruction-item">
                      💻 <strong>Code & Concepts:</strong> Be prepared to discuss implementation details, best practices, and architectural decisions.
                    </Typography>
                    <Typography variant="body1" className="instruction-item">
                      🧠 <strong>Problem Solving:</strong> Explain your thought process when approaching technical challenges.
                    </Typography>
                    <Typography variant="body1" className="instruction-item">
                      📈 <strong>Performance & Scale:</strong> Discuss optimization strategies and trade-offs in your solutions.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" className="instruction-item">
                      🎯 <strong>STAR Method:</strong> Structure answers with Situation, Task, Action, Result.
                    </Typography>
                    <Typography variant="body1" className="instruction-item">
                      📝 <strong>Specific Examples:</strong> Share real experiences from your work or projects.
                    </Typography>
                    <Typography variant="body1" className="instruction-item">
                      💬 <strong>Voice or Text:</strong> You can respond using voice recording or typing.
                    </Typography>
                    <Typography variant="body1" className="instruction-item">
                      🎓 <strong>Be Authentic:</strong> Share both successes and learning experiences.
                    </Typography>
                  </>
                )}
              </div>
              
              <Button 
                onClick={handleCloseInstructions}
                variant="primary"
                className="close-instructions-btn"
              >
                Start Interview
              </Button>
            </div>
          </div>
        )}

        {/* Show content notification */}
        {showContentNotification && hasRequiredContent && !showInstructions && (
          <div className="content-notification">
            <div className="notification-content glass-card">
              <button 
                className="close-notification-btn"
                onClick={handleCloseContentNotification}
              >
                ×
              </button>
              <Typography variant="body2" className="notification-text">
                {interviewType === 'technical' 
                  ? '🔧 Technical requirements loaded - the interviewer will focus on your specified tech stack'
                  : '📄 Resume/job description analyzed - questions will be tailored to your background'
                }
              </Typography>
            </div>
          </div>
        )}

        {/* Main chat area */}
        {isInitialized && (
          <>
            <MessageList 
              messages={messages}
              userAvatar={userAvatar}
              teacherAvatar={teacherAvatar}
            />
            
            {/* Interview completion message */}
            {interviewComplete && (
              <div className="interview-complete-section">
                <div className="completion-card glass-card">
                  <Typography variant="h3" className="completion-title">
                    {interviewType === 'technical' ? 'Technical Interview Complete!' : 'Interview Complete!'}
                  </Typography>
                  {interviewScore && (
                    <Typography variant="h2" className="interview-score">
                      Final Score: {interviewScore}
                    </Typography>
                  )}
                  <Button
                    onClick={handleRestartInterview}
                    variant="primary"
                    className="restart-btn"
                  >
                    Practice Again
                  </Button>
                  <Button
                    onClick={onBackClick}
                    variant="secondary"
                    className="back-btn"
                  >
                    Back to Home
                  </Button>
                </div>
              </div>
            )}
            
            <ChatFooter interviewComplete={interviewComplete} />
          </>
        )}
        
        {/* Error display */}
        {error && (
          <div className="error-section">
            <Typography variant="body1" color="error" align="center">
              {error}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatTemplate; 